"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Typography } from "@/shared/ui/atoms";
import { CustomButton } from "@/shared/ui/molecules";
import useHospitalUIStore from "@/widgets/hospital-detail/model/store";
import { HOSPITAL_DETAIL_ROOT_ID, HOSPITAL_HEADER_FALLBACK_HEIGHT, HOSPITAL_SECTION_NAV_ID, getHospitalSectionScrollOffset, hospitalSectionElementId, scrollToHospitalSection, type HospitalSectionKey } from "@/widgets/hospital-detail/lib/sections";
export type HospitalNavSection = {
  key: HospitalSectionKey;
  count?: string;
};
interface HospitalSectionNavProps {
  sections: HospitalNavSection[];
}

/**
 * 문서 안 위치를 보여 주는 내비. 탭이 아니다 —
 * 눌러도 본문이 교체되지 않고 해당 마디로 스크롤할 뿐이고, 스크롤로도 강조가 따라 움직인다.
 *
 * 아래 조각들이 헤더+내비에 가리지 않도록 실제 높이를 재서
 * `--hospital-sticky-top` 으로 흘려보낸다(리뷰 정렬 바 같은 하위 sticky 요소가 이 값을 쓴다).
 */
const HospitalSectionNav = t0 => {
  const $ = _c(34);
  const {
    sections
  } = t0;
  const t = useTranslations();
  const navRef = useRef(null);
  const [scrollTarget, setScrollTarget] = useState(null);
  const {
    activeSection,
    setActiveSection
  } = useHospitalUIStore();
  const [headerHeight, setHeaderHeight] = useState(HOSPITAL_HEADER_FALLBACK_HEIGHT);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const header = document.querySelector("header.sticky");
      if (!header) {
        return;
      }
      const observer = new ResizeObserver(() => {
        setHeaderHeight(Math.ceil(header.getBoundingClientRect().height));
      });
      observer.observe(header);
      return () => observer.disconnect();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== headerHeight || $[3] !== sections) {
    t3 = [headerHeight, sections];
    $[2] = headerHeight;
    $[3] = sections;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(_temp, t3);
  let t4;
  let t5;
  if ($[5] !== sections || $[6] !== setActiveSection) {
    t4 = () => {
      if (sections.length === 0) {
        return;
      }
      let frame = 0;
      const evaluate = () => {
        frame = 0;
        const boundary = getHospitalSectionScrollOffset() + 8;
        let current = sections[0].key;
        for (const section of sections) {
          const element = document.getElementById(hospitalSectionElementId(section.key));
          if (!element) {
            continue;
          }
          if (element.getBoundingClientRect().top <= boundary) {
            current = section.key;
          }
        }
        setActiveSection(current);
      };
      const onScroll = () => {
        if (frame) {
          return;
        }
        frame = window.requestAnimationFrame(evaluate);
      };
      frame = window.requestAnimationFrame(evaluate);
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      window.addEventListener("resize", onScroll);
      return () => {
        if (frame) {
          window.cancelAnimationFrame(frame);
        }
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      };
    };
    t5 = [sections, setActiveSection];
    $[5] = sections;
    $[6] = setActiveSection;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== scrollTarget) {
    t6 = () => {
      if (!scrollTarget) {
        return;
      }
      const root = document.getElementById(HOSPITAL_DETAIL_ROOT_ID);
      if (!root) {
        return;
      }
      const observer_0 = new ResizeObserver(() => scrollToHospitalSection(scrollTarget, "instant"));
      observer_0.observe(root);
      const release = () => setScrollTarget(null);
      const events = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
      events.forEach(event => window.addEventListener(event, release, {
        passive: true
      }));
      return () => {
        observer_0.disconnect();
        events.forEach(event_0 => window.removeEventListener(event_0, release));
      };
    };
    t7 = [scrollTarget];
    $[9] = scrollTarget;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[12] !== activeSection) {
    t8 = () => {
      const item = navRef.current?.querySelector(`[data-section="${activeSection}"]`);
      const list = item?.parentElement;
      if (!item || !list) {
        return;
      }
      const itemRect = item.getBoundingClientRect();
      const listRect = list.getBoundingClientRect();
      const left = itemRect.left < listRect.left ? itemRect.left - listRect.left : itemRect.right > listRect.right ? itemRect.right - listRect.right : 0;
      if (left !== 0) {
        list.scrollBy({
          left,
          behavior: "smooth"
        });
      }
    };
    t9 = [activeSection];
    $[12] = activeSection;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useEffect(t8, t9);
  if (sections.length === 0) {
    return null;
  }
  let t10;
  if ($[15] !== t) {
    t10 = t("sectionNav.label");
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== headerHeight) {
    t11 = {
      top: headerHeight
    };
    $[17] = headerHeight;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== activeSection || $[20] !== sections || $[21] !== setActiveSection || $[22] !== t) {
    let t13;
    if ($[24] !== activeSection || $[25] !== setActiveSection || $[26] !== t) {
      t13 = section_0 => {
        const isActive = activeSection === section_0.key;
        return <li key={section_0.key} data-section={section_0.key} className="flex-shrink-0"><CustomButton className={`relative flex items-center gap-1 whitespace-nowrap px-3 py-3.5 transition-colors ${isActive ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`} onClick={() => {
            setActiveSection(section_0.key);
            setScrollTarget(section_0.key);
            scrollToHospitalSection(section_0.key, "instant");
          }} ariaLabel={t(`sectionNav.${section_0.key}`)}><Typography as="span" size={14} lineHeight={20} className="font-semibold">{t(`sectionNav.${section_0.key}`)}</Typography>{section_0.count ? <Typography as="span" size={13} lineHeight={20} className="font-medium tabular-nums">{section_0.count}</Typography> : null}<span aria-hidden="true" className={`absolute inset-x-2 bottom-0 h-0.5 rounded-full transition-colors ${isActive ? "bg-primary-600" : "bg-transparent"}`} /></CustomButton></li>;
      };
      $[24] = activeSection;
      $[25] = setActiveSection;
      $[26] = t;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    t12 = sections.map(t13);
    $[19] = activeSection;
    $[20] = sections;
    $[21] = setActiveSection;
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <ul className="hospital-detail-container flex gap-1 overflow-x-auto scrollbar-hide">{t12}</ul>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t13) {
    t14 = <nav id={HOSPITAL_SECTION_NAV_ID} ref={navRef} aria-label={t10} className="sticky z-[100] w-full border-b border-gray-100 bg-white/95 backdrop-blur" style={t11}>{t13}</nav>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
export default HospitalSectionNav;
function _temp() {
  document.getElementById(HOSPITAL_DETAIL_ROOT_ID)?.style.setProperty("--hospital-sticky-top", `${getHospitalSectionScrollOffset()}px`);
}